import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from "@angular/core";
import { UntypedFormArray, UntypedFormGroup } from "@angular/forms";

export type Ets1ReminderStatus = "satisfactory" | "non_satisfactory" | "na";

@Component({
	selector: "app-ets1-rating-reminder-notes",
	templateUrl: "./ets1-rating-reminder-notes.component.html",
	styleUrls: ["./ets1-rating-reminder-notes.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class Ets1RatingReminderNotesComponent {
	@Input({ required: true }) reminderNoteResponses!: UntypedFormArray;

	@Input() readOnly = false;

	/** Scroll/highlight target from the Step 3 gaps checklist. */
	@Input() highlightNoteId: string | null = null;

	@Output() statusChanged = new EventEmitter<void>();

	@Output() textChanged = new EventEmitter<void>();

	readonly maxNoteLength = 1000;

	readonly statusOptions: Array<{ value: Ets1ReminderStatus; label: string }> = [
		{ value: "satisfactory", label: "Satisfactory" },
		{ value: "non_satisfactory", label: "Unsatisfactory" },
		{ value: "na", label: "N/A" },
	];

	constructor(private readonly cd: ChangeDetectorRef) {}

	get responses(): UntypedFormGroup[] {
		return (this.reminderNoteResponses?.controls ?? []) as UntypedFormGroup[];
	}

	setStatus(group: UntypedFormGroup, status: Ets1ReminderStatus): void {
		if (this.readOnly) return;
		group.get("status")?.setValue(status);
		group.get("status")?.markAsDirty();
		group.get("status")?.markAsTouched();
		this.cd.markForCheck();
		this.statusChanged.emit();
	}

	onNoteTextChanged(): void {
		if (this.readOnly) return;
		this.cd.markForCheck();
		this.textChanged.emit();
	}

	isActive(group: UntypedFormGroup, status: Ets1ReminderStatus): boolean {
		return group.get("status")?.value === status;
	}

	getNoteText(group: UntypedFormGroup): string {
		return String(group.get("text")?.value ?? "").trim();
	}

	noteScrollTargetId(group: UntypedFormGroup): string {
		const noteId = String(group.get("noteId")?.value ?? "").trim();
		return noteId ? `ts1-review-reminderNotes-${noteId}` : "";
	}

	isNoteHighlighted(group: UntypedFormGroup): boolean {
		const noteId = String(group.get("noteId")?.value ?? "").trim();
		return !!noteId && noteId === this.highlightNoteId;
	}

	showStatusRequiredError(group: UntypedFormGroup): boolean {
		const status = group.get("status");
		return !!(group.get("requireStatus")?.value && status?.hasError("required") && status.touched);
	}
}
